import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import { Helmet } from 'react-helmet';
import { toast } from 'react-toastify';
import proxy from '../../api/axios';
import { getToken } from '../../common/utils';

const ACCENT = '#137274';

const ROLE_LABEL = {
  'problem-setter': 'Problem Setter',
  'content-writer': 'Content Writer',
  'dsa-mentor': 'DSA Mentor',
  'swe-intern': 'SWE Intern',
  'sde-1': 'SDE-1',
};

const STATUS_COLOR = {
  new: '#d97706',
  reviewing: '#2563eb',
  shortlisted: '#137274',
  rejected: '#8b949e',
  hired: '#1a7f37',
};

const ManageCareers = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const border = isDark ? '#30363d' : 'rgba(15, 60, 70, 0.12)';
  const surface = isDark ? '#161b22' : '#ffffff';
  const muted = isDark ? '#8b949e' : '#5f6b73';
  const pageBg = isDark
    ? '#0f1419'
    : 'linear-gradient(180deg, #eef6f8 0%, #f7f9fb 40%, #ffffff 100%)';

  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('new');
  const [role, setRole] = useState('all');
  const [q, setQ] = useState('');
  const [detail, setDetail] = useState(null);
  const [notes, setNotes] = useState('');
  const [busyId, setBusyId] = useState(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await proxy.get('/careers/admin/applications', {
        params: {
          status,
          role,
          q: q.trim() || undefined,
        },
      });
      setItems(data.items || []);
      setCounts(data.counts || {});
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load applications');
    } finally {
      setLoading(false);
    }
  }, [status, role, q]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const openDetail = (item) => {
    setDetail(item);
    setNotes(item.adminNotes || '');
  };

  const saveStatus = async (id, nextStatus) => {
    setBusyId(id);
    try {
      await proxy.patch(`/careers/admin/applications/${id}`, {
        status: nextStatus,
        adminNotes: notes,
      });
      toast.success('Updated');
      setDetail(null);
      fetchList();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Update failed');
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this application permanently?')) return;
    setBusyId(id);
    try {
      await proxy.delete(`/careers/admin/applications/${id}`);
      toast.success('Deleted');
      setDetail(null);
      fetchList();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Delete failed');
    } finally {
      setBusyId(null);
    }
  };

  const downloadResume = async (id, filename) => {
    try {
      const token = getToken();
      const base = process.env.REACT_APP_API_URL || '/api';
      const res = await fetch(`${base}/careers/admin/applications/${id}/resume`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'resume.pdf';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Could not download resume');
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', background: pageBg, px: { xs: 1.5, sm: 2.5 }, py: 2.5 }}>
      <Helmet>
        <title>Manage Careers | Admin</title>
      </Helmet>

      <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
            mb: 2.5,
          }}
        >
          <Box>
            <Typography sx={{ fontWeight: 900, fontSize: '1.4rem', letterSpacing: '-0.03em' }}>
              Career applications
            </Typography>
            <Typography variant="body2" sx={{ color: muted }}>
              Review resumes and update hiring status
            </Typography>
          </Box>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchList} sx={{ border: `1px solid ${border}` }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          {['new', 'reviewing', 'shortlisted', 'rejected', 'hired', 'all'].map((s) => (
            <Chip
              key={s}
              clickable
              label={
                s === 'all'
                  ? 'All'
                  : `${s} (${counts[s] ?? 0})`
              }
              onClick={() => setStatus(s)}
              sx={{
                fontWeight: 700,
                textTransform: 'capitalize',
                bgcolor: status === s ? ACCENT : surface,
                color: status === s ? '#fff' : 'inherit',
                border: `1px solid ${status === s ? ACCENT : border}`,
              }}
            />
          ))}
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1.5,
            mb: 2.5,
            p: 1.75,
            borderRadius: 2,
            border: `1px solid ${border}`,
            bgcolor: surface,
          }}
        >
          <TextField
            size="small"
            placeholder="Search name, email…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && fetchList()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: muted }} />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 220, flex: 1 }}
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Role</InputLabel>
            <Select label="Role" value={role} onChange={(e) => setRole(e.target.value)}>
              <MenuItem value="all">All roles</MenuItem>
              {Object.entries(ROLE_LABEL).map(([id, label]) => (
                <MenuItem key={id} value={id}>
                  {label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            onClick={fetchList}
            sx={{ textTransform: 'none', fontWeight: 700, bgcolor: ACCENT }}
          >
            Search
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress sx={{ color: ACCENT }} />
          </Box>
        ) : items.length === 0 ? (
          <Box
            sx={{
              textAlign: 'center',
              py: 8,
              borderRadius: 2.5,
              border: `1px dashed ${border}`,
              bgcolor: surface,
            }}
          >
            <Typography sx={{ fontWeight: 800, mb: 0.5 }}>No applications</Typography>
            <Typography variant="body2" sx={{ color: muted }}>
              Try another filter — or share /careers publicly.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            {items.map((item) => {
              const id = item.id || item._id;
              return (
                <Box
                  key={id}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: `1px solid ${border}`,
                    bgcolor: surface,
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1.5,
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 0.5 }}>
                      <Chip
                        size="small"
                        label={ROLE_LABEL[item.role] || item.role}
                        sx={{
                          height: 22,
                          fontWeight: 800,
                          fontSize: '0.68rem',
                          bgcolor: alpha(ACCENT, isDark ? 0.22 : 0.1),
                          color: ACCENT,
                        }}
                      />
                      <Chip
                        size="small"
                        label={item.status}
                        sx={{
                          height: 22,
                          fontWeight: 800,
                          fontSize: '0.68rem',
                          textTransform: 'capitalize',
                          bgcolor: alpha(STATUS_COLOR[item.status] || muted, 0.15),
                          color: STATUS_COLOR[item.status] || muted,
                        }}
                      />
                    </Box>
                    <Typography sx={{ fontWeight: 800 }}>{item.name}</Typography>
                    <Typography variant="body2" sx={{ color: muted, fontSize: '0.82rem' }}>
                      {item.email} · {item.phone}
                      {item.experience ? ` · ${item.experience}` : ''}
                    </Typography>
                    <Typography variant="caption" sx={{ color: muted }}>
                      {item.createdAt ? new Date(item.createdAt).toLocaleString() : ''}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Tooltip title="View">
                      <IconButton onClick={() => openDetail(item)}>
                        <VisibilityOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Download resume">
                      <IconButton
                        onClick={() =>
                          downloadResume(id, item.resume?.originalName)
                        }
                      >
                        <DownloadOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        color="error"
                        disabled={busyId === id}
                        onClick={() => remove(id)}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>

      <Dialog open={Boolean(detail)} onClose={() => setDetail(null)} maxWidth="sm" fullWidth>
        {detail && (
          <>
            <DialogTitle sx={{ fontWeight: 800 }}>
              {detail.name} · {ROLE_LABEL[detail.role] || detail.role}
            </DialogTitle>
            <DialogContent dividers>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Email:</strong> {detail.email}
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Phone:</strong> {detail.phone}
              </Typography>
              {detail.linkedin && (
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>LinkedIn:</strong> {detail.linkedin}
                </Typography>
              )}
              {detail.portfolio && (
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Portfolio:</strong> {detail.portfolio}
                </Typography>
              )}
              {detail.location && (
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Location:</strong> {detail.location}
                </Typography>
              )}
              {detail.experience && (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Experience:</strong> {detail.experience}
                </Typography>
              )}
              <Typography sx={{ fontWeight: 800, mt: 1.5, mb: 0.75 }}>Cover note</Typography>
              <Typography
                variant="body2"
                sx={{
                  whiteSpace: 'pre-wrap',
                  color: muted,
                  mb: 2,
                  p: 1.5,
                  borderRadius: 1.5,
                  bgcolor: isDark ? alpha('#fff', 0.04) : alpha(ACCENT, 0.04),
                }}
              >
                {detail.coverLetter}
              </Typography>
              <TextField
                fullWidth
                size="small"
                label="Admin notes"
                multiline
                minRows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                sx={{ mb: 1.5 }}
              />
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={detail.status}
                  onChange={(e) => setDetail((d) => ({ ...d, status: e.target.value }))}
                >
                  {['new', 'reviewing', 'shortlisted', 'rejected', 'hired'].map((s) => (
                    <MenuItem key={s} value={s}>
                      {s}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </DialogContent>
            <DialogActions sx={{ px: 2.5, py: 1.5, gap: 1 }}>
              <Button
                startIcon={<DownloadOutlinedIcon />}
                onClick={() =>
                  downloadResume(detail.id || detail._id, detail.resume?.originalName)
                }
                sx={{ textTransform: 'none', mr: 'auto' }}
              >
                Resume
              </Button>
              <Button onClick={() => setDetail(null)} sx={{ textTransform: 'none' }}>
                Cancel
              </Button>
              <Button
                variant="contained"
                disabled={busyId === (detail.id || detail._id)}
                onClick={() => saveStatus(detail.id || detail._id, detail.status)}
                sx={{ textTransform: 'none', fontWeight: 800, bgcolor: ACCENT }}
              >
                Save
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default ManageCareers;
